import {
  useCallback,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from 'utils/cn';

type HoverTooltipProps = {
  label: string;
  enabled?: boolean;
  children: ReactNode;
  className?: string;
};

export function HoverTooltip({
  label,
  enabled = true,
  children,
  className,
}: HoverTooltipProps) {
  const tooltipId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setCoords({
      top: rect.top + rect.height / 2,
      left: rect.right + 10,
    });
  }, []);

  const show = useCallback(() => {
    if (!enabled) return;
    updatePosition();
    setVisible(true);
  }, [enabled, updatePosition]);

  const hide = useCallback(() => {
    setVisible(false);
  }, []);

  const onMouseEnter = () => {
    show();
  };

  const onMouseLeave = () => {
    hide();
  };

  const onFocus = (event: FocusEvent<HTMLDivElement>) => {
    if (event.target !== anchorRef.current && !anchorRef.current?.contains(event.target as Node)) {
      return;
    }
    show();
  };

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (anchorRef.current?.contains(event.relatedTarget as Node)) return;
    hide();
  };

  return (
    <div
      ref={anchorRef}
      className={cn('relative w-full', className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {children}
      {enabled && visible && typeof document !== 'undefined'
        ? createPortal(
            <div
              id={tooltipId}
              role="tooltip"
              className={cn(
                'hover-tooltip pointer-events-none fixed z-[var(--z-tooltip)]',
                'whitespace-nowrap rounded-lg border border-border bg-surface px-2.5 py-1.5',
                'text-xs font-semibold text-content shadow-modal',
              )}
              style={{
                top: coords.top,
                left: coords.left,
                transform: 'translateY(-50%)',
              }}
            >
              {label}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
